import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import {
  Row,
  Col,
  Card,
  CardBody,
  Form,
  Label,
  Container,
  Button,
} from "reactstrap"
import { useFormik } from "formik"
import * as Yup from "yup"
import Breadcrumbs from "components/Common/Breadcrumb"
import InputField from "components/Common/InputField"
import ButtonTooltip from "components/Common/ButtonTooltip"
import { useNavigate, useParams } from "react-router-dom"
import avatar3 from "assets/images/users/avatar-3.jpg"
import {
  useCreateVehicleImporter,
  useEditVehicleImporter,
  useVehicleImporter,
} from "hooks/useVehicleImporter"
import {
  useVehicleImporterStaffs,
  useCreateVehicleImporterStaff,
  useEditVehicleImporterStaff,
  useDeleteVehicleImporterStaff,
} from "hooks/useVehicleImporterStaff"
import { toast } from "react-toastify"
import { useUploadImages } from "hooks/imageHook"
import { useListParams } from "hooks/useListParams"

const VehicleImporterAddEdit = () => {
  document.title = "Vehicle Importer Form | Aurobil Cars"

  const navigate = useNavigate()
  const { importerId } = useParams()
  const { params } = useListParams()
  const isEdit = !!importerId

  // Hooks
  const { data: importerResponse, isLoading } = useVehicleImporter(importerId, {
    enabled: isEdit,
  })
  const { mutateAsync: createImporter, isPending: createLoading } =
    useCreateVehicleImporter()
  const { mutateAsync: editImporterMutate, isPending: editLoading } =
    useEditVehicleImporter()
  const uploadLogoMutation = useUploadImages()

  // Staff (authorized persons) CRUD — used to sync after importer save
  const { data: staffResponse } = useVehicleImporterStaffs(
    isEdit ? importerId : null,
    { all: true },
    isEdit
  )
  const { mutateAsync: createStaffMutate } = useCreateVehicleImporterStaff(
    importerId
  )
  const { mutateAsync: editStaffMutate } = useEditVehicleImporterStaff(
    importerId
  )
  const { mutateAsync: deleteStaffMutate } = useDeleteVehicleImporterStaff(
    importerId
  )

  // Mobile repeater
  const [mobiles, setMobiles] = useState([{ id: 1, number: "" }])

  // Address state - defaults to single address for new, multi for edit if multiple exist
  const [singleAddress, setSingleAddress] = useState(true)

  // Authorized Persons repeater
  const [authorizedPersons, setAuthorizedPersons] = useState([
    { id: 1, dbId: null, name: "", designation: "", mobiles: [{ id: 1, number: "" }] },
  ])
  const removedStaffDbIdsRef = useRef([])

  // Addresses state - always maintain at least one address
  const [addresses, setAddresses] = useState([
    {
      id: 1,
      address: "",
      showroom_name: "",
      showroom_address: "",
      email: "",
      mobile: "",
    },
  ])

  // Logo preview
  const [logoPreview, setLogoPreview] = useState(null)
  const [logoFile, setLogoFile] = useState(null)
  const [logoDeleted, setLogoDeleted] = useState(false)

  // Helper functions
  const parseLogo = (logo, field = "url") => {
    return logo?.length ? logo[0]?.[field] : null
  }

  const generateId = () => Date.now() + Math.random()

  // Mobile handlers
  const addMobile = () =>
    setMobiles([...mobiles, { id: generateId(), number: "" }])

  const removeMobile = id => {
    if (mobiles.length > 1) setMobiles(mobiles.filter(m => m.id !== id))
  }

  const handleMobileChange = (id, value) => {
    setMobiles(mobiles.map(m => (m.id === id ? { ...m, number: value } : m)))
  }

  // Authorized Persons handlers
  const addAuthorizedPerson = () =>
    setAuthorizedPersons([
      ...authorizedPersons,
      {
        id: generateId(),
        dbId: null,
        name: "",
        designation: "",
        mobiles: [{ id: generateId(), number: "" }],
      },
    ])

  const removeAuthorizedPerson = id => {
    if (authorizedPersons.length <= 1) return
    const target = authorizedPersons.find(p => p.id === id)
    if (target?.dbId) {
      removedStaffDbIdsRef.current = [
        ...removedStaffDbIdsRef.current,
        target.dbId,
      ]
    }
    setAuthorizedPersons(authorizedPersons.filter(p => p.id !== id))
  }

  const handleAuthorizedPersonChange = (id, field, value) => {
    setAuthorizedPersons(
      authorizedPersons.map(p => (p.id === id ? { ...p, [field]: value } : p))
    )
  }

  const addAuthorizedPersonMobile = personId => {
    setAuthorizedPersons(
      authorizedPersons.map(p =>
        p.id === personId
          ? { ...p, mobiles: [...p.mobiles, { id: generateId(), number: "" }] }
          : p
      )
    )
  }

  const removeAuthorizedPersonMobile = (personId, mobileId) => {
    setAuthorizedPersons(
      authorizedPersons.map(p =>
        p.id === personId
          ? p.mobiles.length > 1
            ? { ...p, mobiles: p.mobiles.filter(m => m.id !== mobileId) }
            : p
          : p
      )
    )
  }

  const handleAuthorizedPersonMobileChange = (personId, mobileId, value) => {
    setAuthorizedPersons(
      authorizedPersons.map(p =>
        p.id === personId
          ? {
              ...p,
              mobiles: p.mobiles.map(m =>
                m.id === mobileId ? { ...m, number: value } : m
              ),
            }
          : p
      )
    )
  }

  // Address handlers
  const addAddress = () => {
    const newAddress = {
      id: generateId(),
      address: "",
      showroom_name: "",
      showroom_address: "",
      email: "",
      mobile: "",
    }
    setAddresses([...addresses, newAddress])
    // Switch to multi-address mode when adding more addresses
    setSingleAddress(false)
  }

  const removeAddress = id => {
    if (addresses.length > 1) {
      const newAddresses = addresses.filter(a => a.id !== id)
      setAddresses(newAddresses)
      // If only one address remains, show single address UI
      if (newAddresses.length === 1) {
        setSingleAddress(true)
      }
    }
  }

  const handleAddressChange = (id, field, value) => {
    setAddresses(
      addresses.map(a => (a.id === id ? { ...a, [field]: value } : a))
    )
  }

  // Toggle between single and multi address modes
  const toggleAddressMode = () => {
    if (singleAddress) {
      // Switching to multi-address mode
      setSingleAddress(false)
      // Keep the current address as the first one
      if (addresses.length === 1 && addresses[0].address) {
        // Keep existing address
      }
    } else {
      // Switching to single address mode
      setSingleAddress(true)
      // If multiple addresses exist, keep only the first one
      if (addresses.length > 1) {
        setAddresses([addresses[0]])
      }
    }
  }

  // Get the primary address (first address)
  const getPrimaryAddress = () => {
    return (
      addresses[0] || {
        id: generateId(),
        address: "",
        showroom_name: "",
        showroom_address: "",
        email: "",
        mobile: "",
      }
    )
  }

  // Update primary address in single mode
  const updatePrimaryAddress = (field, value) => {
    if (addresses.length === 0) {
      // Create first address if none exists
      setAddresses([{ id: generateId(), [field]: value }])
    } else {
      const updatedAddresses = [...addresses]
      updatedAddresses[0] = { ...updatedAddresses[0], [field]: value }
      setAddresses(updatedAddresses)
    }
  }

  // Logo handlers
  const handleLogoChange = e => {
    const file = e.target.files[0]
    if (file) {
      setLogoPreview(file)
      setLogoFile(file)
      setLogoDeleted(false)
    }
  }

  const removeLogo = () => {
    setLogoPreview(null)
    setLogoFile(null)
    setLogoDeleted(true)
  }

  const importer = importerResponse?.data ?? {}

  // Initialize form based on edit mode and existing data
  const initialValues = useMemo(
    () => ({
      name: importer?.name || "",
      slug: importer?.slug || "",
      email: importer?.email || "",
      website: importer?.website || "",
    }),
    [importer]
  )

  // Formik setup
  const validationSchema = Yup.object({
    name: Yup.string().required("Name is required"),
    email: Yup.string().email("Invalid email"),
    website: Yup.string().url("Invalid URL"),
  })

  const onSubmit = useCallback(
    async (values, { setSubmitting }) => {
      try {
        let uploadedLogo = logoPreview
        const formData = new FormData()

        // Upload new logo if a new file is selected
        if (logoFile instanceof File) {
          formData.append("images[]", logoFile)
        }

        // Delete existing logo if removed
        if (logoDeleted && importer?.logo && !(logoFile instanceof File)) {
          const logoId = parseLogo(importer.logo, "id")
          if (logoId) {
            formData.append("delete_file_ids[]", logoId)
          }
          uploadedLogo = null // logo is deleted
        }

        // Upload logo if needed
        if (formData.has("images[]") || formData.has("delete_file_ids[]")) {
          const uploadRes = await uploadLogoMutation.mutateAsync(formData)
          if (uploadRes.success && uploadRes.data?.length) {
            uploadedLogo = uploadRes.data
          }
        }

        // Prepare addresses for submission
        let addressesToSubmit = addresses

        // If in single address mode, only submit the first address
        if (singleAddress) {
          addressesToSubmit = [
            addresses[0] || {
              address: "",
              showroom_name: "",
              showroom_address: "",
              email: "",
              mobile: "",
            },
          ]
        }

        // Filter out empty addresses
        const validAddresses = addressesToSubmit.filter(
          addr =>
            addr.address ||
            addr.showroom_name ||
            addr.showroom_address ||
            addr.email ||
            addr.mobile
        )

        // Prepare payload
        const payload = {
          name: values.name.trim(),
          email: values.email?.trim() || null,
          mobile: mobiles.map(m => m.number.trim()).filter(Boolean),
          website: values.website?.trim() || null,
          address: validAddresses.length > 0 ? validAddresses : null,
          logo: Array.isArray(uploadedLogo)
            ? uploadedLogo
            : importer?.logo && !logoDeleted
            ? importer.logo
            : null,
        }

        // Call API
        const res = isEdit
          ? await editImporterMutate({ id: importerId, ...payload })
          : await createImporter(payload)

        const newImporterId =
          res?.data?.id ?? res?.data?.data?.id ?? importerId

        // Sync staff (authorized persons) via dedicated endpoints
        if (isEdit && newImporterId) {
          const cleanedPersons = authorizedPersons
            .map(person => {
              const validMobiles = person.mobiles
                .map(m => String(m.number).trim())
                .filter(Boolean)
              if (
                person.name.trim() ||
                person.designation.trim() ||
                validMobiles.length > 0
              ) {
                return {
                  dbId: person.dbId ?? null,
                  name: person.name.trim(),
                  designation: person.designation.trim() || null,
                  mobile: validMobiles,
                }
              }
              return null
            })
            .filter(Boolean)

          for (const person of cleanedPersons) {
            if (person.dbId) {
              await editStaffMutate({
                id: person.dbId,
                name: person.name,
                designation: person.designation,
                mobile: person.mobile,
              })
            } else {
              await createStaffMutate({
                name: person.name,
                designation: person.designation,
                mobile: person.mobile,
              })
            }
          }

          for (const removedId of removedStaffDbIdsRef.current) {
            try {
              await deleteStaffMutate(removedId)
            } catch (e) {
              console.warn("Failed to delete removed staff", removedId, e)
            }
          }
          removedStaffDbIdsRef.current = []
        }

        toast.success(
          res?.message ??
            `Importer ${isEdit ? "updated" : "created"} successfully.`
        )
        console.log(res)

        navigate(
          params?.ref === "list"
            ? "/importer-list?ref=list"
            : params?.ref === "self"
            ? `/importer-list/${res?.data?.slug || importerId}/view?ref=self`
            : "/importer-list"
        )
      } catch (error) {
        console.error("Failed to save importer:", error)
        toast.error(error.message || "Failed to save importer.")
      } finally {
        setSubmitting(false)
      }
    },
    [
      isEdit,
      importerId,
      mobiles,
      addresses,
      authorizedPersons,
      logoFile,
      logoPreview,
      logoDeleted,
      singleAddress,
      createImporter,
      editImporterMutate,
      navigate,
      uploadLogoMutation,
      importer,
      createStaffMutate,
      editStaffMutate,
      deleteStaffMutate,
    ]
  )

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema,
    onSubmit,
  })

  // Prefill data when editing
  useEffect(() => {
    if (isEdit && importerResponse?.data && !isLoading) {
      const importer = importerResponse.data

      // Prefill mobiles
      if (importer.mobile && Array.isArray(importer.mobile)) {
        setMobiles(
          importer.mobile.length
            ? importer.mobile.map(num => ({
                id: generateId(),
                number: num,
              }))
            : [{ id: generateId(), number: "" }]
        )
      }

      // Prefill authorized persons from staff table (new source of truth)
      const staffList = staffResponse?.data?.data || staffResponse?.data || []
      if (Array.isArray(staffList)) {
        setAuthorizedPersons(
          staffList.length > 0
            ? staffList.map(person => ({
                id: generateId(),
                dbId: person.id,
                name: person.name || "",
                designation: person.designation || "",
                mobiles:
                  Array.isArray(person.mobile) && person.mobile.length
                    ? person.mobile.map(num => ({
                        id: generateId(),
                        number: num,
                      }))
                    : [{ id: generateId(), number: "" }],
              }))
            : [
                {
                  id: generateId(),
                  dbId: null,
                  name: "",
                  designation: "",
                  mobiles: [{ id: generateId(), number: "" }],
                },
              ]
        )
      }

      // Prefill addresses
      if (importer.address && Array.isArray(importer.address)) {
        const importerAddresses = importer.address
        setAddresses(
          importerAddresses.length > 0
            ? importerAddresses.map(addr => ({
                id: addr.id || generateId(),
                address: addr.address || "",
                showroom_name: addr.showroom_name || "",
                showroom_address: addr.showroom_address || "",
                email: addr.email || "",
                mobile: addr.mobile || "",
              }))
            : [
                {
                  id: generateId(),
                  address: "",
                  showroom_name: "",
                  showroom_address: "",
                  email: "",
                  mobile: "",
                },
              ]
        )

        // Determine address mode based on number of addresses
        if (importerAddresses.length === 1) {
          setSingleAddress(true)
        } else if (importerAddresses.length > 1) {
          setSingleAddress(false)
        }
      }

      // Prefill logo
      if (importer?.logo) {
        setLogoPreview(parseLogo(importer.logo))
      }
    }
  }, [isEdit, importerResponse, isLoading])

  // Create object URL for logo preview
  useEffect(() => {
    let objectUrl
    if (logoPreview instanceof File) {
      objectUrl = URL.createObjectURL(logoPreview)
      return () => URL.revokeObjectURL(objectUrl)
    }
  }, [logoPreview])

  // Reset form to initial state
  const resetForm = () => {
    formik.resetForm()
    setMobiles([{ id: generateId(), number: "" }])
    setAuthorizedPersons([
      {
        id: generateId(),
        name: "",
        designation: "",
        mobiles: [{ id: generateId(), number: "" }],
      },
    ])
    setAddresses([
      {
        id: generateId(),
        address: "",
        showroom_name: "",
        showroom_address: "",
        email: "",
        mobile: "",
      },
    ])
    setSingleAddress(true)
    setLogoPreview(null)
    setLogoFile(null)
    setLogoDeleted(false)
  }

  // Button setup
  const buttonText = useMemo(() => {
    const loading = createLoading || editLoading || formik.isSubmitting
    return `${isEdit ? "Save Changes" : "Add Importer"}${loading ? "..." : ""}`
  }, [isEdit, createLoading, editLoading, formik.isSubmitting])

  const isSubmitDisabled = useMemo(
    () => createLoading || editLoading || formik.isSubmitting,
    [createLoading, editLoading, formik.isSubmitting]
  )

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumbs
          title="Vehicle Importers"
          breadcrumbItem="Add / Edit Importer"
        />

        <Row>
          <Col lg={12}>
            <Card>
              <CardBody>
                <Row className="border-bottom d-flex justify-content-between align-items-center pb-3">
                  <Col xs={12} sm={6}>
                    <h5 className="mb-0">
                      {isEdit ? "Edit Importer" : "Add New Importer"}
                    </h5>
                  </Col>
                  <Col xs={12} sm={6} className="text-sm-end mt-2 mt-sm-0">
                    <div>
                      <Button
                        color="primary"
                        onClick={() => navigate("/importer-list")}
                        className="me-1"
                      >
                        Back to List
                      </Button>
                      <Button color="danger" onClick={resetForm}>
                        Reset Form
                      </Button>
                    </div>
                  </Col>
                </Row>

                <Form
                  onSubmit={formik.handleSubmit}
                  encType="multipart/form-data"
                  className="mt-3"
                >
                  {/* === BASIC INFO === */}
                  <Card className="mb-4">
                    <div className="card-header bg-light">
                      <h6 className="mb-0">Basic Information</h6>
                    </div>
                    <CardBody>
                      <Row>
                        <Col md={6} className="mb-3">
                          <Label>Name *</Label>
                          <InputField
                            name="name"
                            value={formik.values.name}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            invalid={
                              !!formik.errors.name && formik.touched.name
                            }
                            feedback={formik.errors.name}
                            disabled={isLoading}
                          />
                        </Col>
                        <Col md={6} className="mb-3">
                          <Label>Email</Label>
                          <InputField
                            name="email"
                            value={formik.values.email}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            invalid={
                              !!formik.errors.email && formik.touched.email
                            }
                            feedback={formik.errors.email}
                            disabled={isLoading}
                          />
                        </Col>
                        <Col md={6} className="mb-3">
                          <Label>Website</Label>
                          <InputField
                            name="website"
                            value={formik.values.website}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            invalid={
                              !!formik.errors.website && formik.touched.website
                            }
                            feedback={formik.errors.website}
                            disabled={isLoading}
                          />
                        </Col>
                        <Col md={6} className="mb-3">
                          <Label>Logo</Label>
                          <InputField
                            type="file"
                            onChange={handleLogoChange}
                            disabled={isLoading}
                            accept="image/*"
                          />
                          {logoPreview && (
                            <div className="mt-2 d-flex align-items-center">
                              <img
                                src={
                                  logoPreview instanceof File
                                    ? URL.createObjectURL(logoPreview)
                                    : logoPreview || avatar3
                                }
                                alt="Logo Preview"
                                className="rounded avatar-sm me-2"
                                style={{
                                  width: "50px",
                                  height: "50px",
                                  objectFit: "cover",
                                }}
                              />
                              <ButtonTooltip
                                type="delete"
                                btnId="remove-logo"
                                onClick={removeLogo}
                                disabled={isLoading}
                              />
                            </div>
                          )}
                          {isEdit &&
                            importer?.logo &&
                            !logoPreview &&
                            !logoDeleted && (
                              <div className="mt-2">
                                <small className="text-muted">
                                  Current logo is saved
                                </small>
                              </div>
                            )}
                        </Col>
                      </Row>
                    </CardBody>
                  </Card>

                  {/* Mobile Numbers */}
                  <Card className="mb-4">
                    <div className="card-header bg-light d-flex justify-content-between align-items-center">
                      <h6 className="mb-0">Mobile Numbers</h6>
                      <ButtonTooltip
                        type="add"
                        btnId="add-mobile"
                        onClick={addMobile}
                        disabled={isLoading}
                      />
                    </div>
                    <CardBody>
                      {mobiles.map((m, index) => (
                        <Row key={m.id} className="mb-2 align-items-center">
                          <Col md={10}>
                            <InputField
                              value={m.number}
                              placeholder="Enter mobile number"
                              onChange={e =>
                                handleMobileChange(m.id, e.target.value)
                              }
                              disabled={isLoading}
                            />
                          </Col>
                          <Col md={2}>
                            {index !== 0 && (
                              <ButtonTooltip
                                type="delete"
                                btnId={`delete-mobile-${String(m.id).replace(
                                  /\W/g,
                                  "_"
                                )}`}
                                onClick={() => removeMobile(m.id)}
                                disabled={isLoading}
                              />
                            )}
                          </Col>
                        </Row>
                      ))}
                    </CardBody>
                  </Card>

                  {/* Authorized Persons */}
                  <Card className="mb-4">
                    <div className="card-header bg-light d-flex justify-content-between align-items-center">
                      <h6 className="mb-0">Authorized Persons</h6>
                      <ButtonTooltip
                        type="add"
                        btnId="add-person"
                        onClick={addAuthorizedPerson}
                        disabled={isLoading}
                      />
                    </div>
                    <CardBody>
                      {authorizedPersons.map((p, index) => (
                        <Card key={p.id} className="mb-3 p-2 border">
                          <Row className="mb-2">
                            <Col md={4}>
                              <InputField
                                placeholder="Name"
                                value={p.name}
                                onChange={e =>
                                  handleAuthorizedPersonChange(
                                    p.id,
                                    "name",
                                    e.target.value
                                  )
                                }
                                disabled={isLoading}
                              />
                            </Col>
                            <Col md={4}>
                              <InputField
                                placeholder="Designation"
                                value={p.designation}
                                onChange={e =>
                                  handleAuthorizedPersonChange(
                                    p.id,
                                    "designation",
                                    e.target.value
                                  )
                                }
                                disabled={isLoading}
                              />
                            </Col>
                            <Col
                              md={4}
                              className="d-flex align-items-center justify-content-end"
                            >
                              {index !== 0 && (
                                <ButtonTooltip
                                  type="delete"
                                  btnId={`delete-person-${String(p.id).replace(
                                    /\W/g,
                                    "_"
                                  )}`}
                                  onClick={() => removeAuthorizedPerson(p.id)}
                                  disabled={isLoading}
                                />
                              )}
                            </Col>
                          </Row>

                          {/* Nested mobiles for authorized person */}
                          <Row className="mt-2">
                            <Col md={12}>
                              <div className="d-flex justify-content-between align-items-center mb-2">
                                <h6 className="mb-0">Contact Numbers</h6>
                                <ButtonTooltip
                                  type="add"
                                  btnId={`add-person-mobile-${String(
                                    p.id
                                  ).replace(/\W/g, "_")}`}
                                  onClick={() =>
                                    addAuthorizedPersonMobile(p.id)
                                  }
                                  disabled={isLoading}
                                />
                              </div>
                              {p.mobiles.map((m, mIndex) => (
                                <Row
                                  key={m.id}
                                  className="mb-1 align-items-center"
                                >
                                  <Col md={10}>
                                    <InputField
                                      value={m.number}
                                      placeholder="Mobile number"
                                      onChange={e =>
                                        handleAuthorizedPersonMobileChange(
                                          p.id,
                                          m.id,
                                          e.target.value
                                        )
                                      }
                                      disabled={isLoading}
                                    />
                                  </Col>
                                  <Col md={2}>
                                    {mIndex !== 0 && (
                                      <ButtonTooltip
                                        type="delete"
                                        btnId={`delete-person-mobile-${String(
                                          m.id
                                        ).replace(/\W/g, "_")}`}
                                        onClick={() =>
                                          removeAuthorizedPersonMobile(
                                            p.id,
                                            m.id
                                          )
                                        }
                                        disabled={isLoading}
                                      />
                                    )}
                                  </Col>
                                </Row>
                              ))}
                            </Col>
                          </Row>
                        </Card>
                      ))}
                    </CardBody>
                  </Card>

                  {/* Addresses */}
                  <Card className="mb-4">
                    <div className="card-header bg-light d-flex justify-content-between align-items-center">
                      <h6 className="mb-0">
                        {singleAddress ? "Primary Address" : "Addresses"}
                      </h6>
                      {!singleAddress && (
                        <ButtonTooltip
                          type="add"
                          btnId="add-address"
                          onClick={addAddress}
                          disabled={isLoading}
                        />
                      )}
                    </div>
                    <CardBody>
                      <div className="mb-3">
                        <Button
                          color={singleAddress ? "info" : "primary"}
                          size="sm"
                          onClick={toggleAddressMode}
                          disabled={isLoading}
                        >
                          {singleAddress
                            ? "Switch to Multiple Addresses"
                            : "Switch to Single Address"}
                        </Button>
                      </div>

                      {singleAddress ? (
                        // Single address mode
                        <Row>
                          <Col md={12} className="mb-3">
                            <Label>Address</Label>
                            <InputField
                              placeholder="Additional address details"
                              value={getPrimaryAddress().address}
                              onChange={e =>
                                updatePrimaryAddress("address", e.target.value)
                              }
                              disabled={isLoading}
                            />
                          </Col>
                        </Row>
                      ) : (
                        // Multi-address mode
                        addresses.map((a, index) => (
                          <Card key={a.id} className="mb-3 p-2 border">
                            <Row className="mb-2">
                              <Col
                                md={12}
                                className="d-flex justify-content-between align-items-center mb-2"
                              >
                                <h6 className="mb-0">Address {index + 1}</h6>
                                {index !== 0 && (
                                  <ButtonTooltip
                                    type="delete"
                                    btnId={`delete-address-${String(
                                      a.id
                                    ).replace(/\W/g, "_")}`}
                                    onClick={() => removeAddress(a.id)}
                                    disabled={isLoading}
                                  />
                                )}
                              </Col>
                              <Col md={6} className="mb-3">
                                <Label>Showroom Name</Label>
                                <InputField
                                  placeholder="e.g., HEAD OFFICE"
                                  value={a.showroom_name}
                                  onChange={e =>
                                    handleAddressChange(
                                      a.id,
                                      "showroom_name",
                                      e.target.value
                                    )
                                  }
                                  disabled={isLoading}
                                />
                              </Col>
                              <Col md={6} className="mb-3">
                                <Label>Showroom Address</Label>
                                <InputField
                                  placeholder="Full address"
                                  value={a.showroom_address}
                                  onChange={e =>
                                    handleAddressChange(
                                      a.id,
                                      "showroom_address",
                                      e.target.value
                                    )
                                  }
                                  disabled={isLoading}
                                />
                              </Col>
                              <Col md={6} className="mb-3">
                                <Label>Email</Label>
                                <InputField
                                  placeholder="Email address"
                                  value={a.email}
                                  onChange={e =>
                                    handleAddressChange(
                                      a.id,
                                      "email",
                                      e.target.value
                                    )
                                  }
                                  disabled={isLoading}
                                />
                              </Col>
                              <Col md={6} className="mb-3">
                                <Label>Mobile</Label>
                                <InputField
                                  placeholder="Mobile number"
                                  value={a.mobile}
                                  onChange={e =>
                                    handleAddressChange(
                                      a.id,
                                      "mobile",
                                      e.target.value
                                    )
                                  }
                                  disabled={isLoading}
                                />
                              </Col>
                            </Row>
                          </Card>
                        ))
                      )}
                    </CardBody>
                  </Card>

                  <div className="text-end">
                    <Button
                      type="submit"
                      color="success"
                      disabled={isSubmitDisabled || isLoading}
                    >
                      {isLoading ? "Loading..." : buttonText}
                    </Button>
                  </div>
                </Form>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  )
}

export default VehicleImporterAddEdit
